// ============================================================
// AdminProgressPage — M12.A (PRD §5.7 tab 8, §7.6 AC-PROG-1..4 + AC-FC-6)
// ============================================================
// 4 tab:
//   - overview:    bảng 10 NV (sortable) · best score tháng · ngày học cuối ·
//                  card mastered · accuracy · reward badges · click → detail
//   - detail:      chi tiết 1 NV: exam history · rules exam · ask AI · flashcard
//                  timeline 14d · topic yếu (<70%) · nút "Reset topic" (AC-FC-6)
//   - leaderboard: toggle period (week/month/quarter) · rank + badge top 3
//   - alerts:      3 rule (inactive_3d, low_accuracy, high_cost) · suggested_msg
//                  · modal gửi Telegram (POST /admin/progress/alerts/remind)
//
// AC-FC-6: reset topic dùng existing api.adminFlashcardResetTopic (X-TOTP-Code).
// ============================================================

import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuthStore } from '../lib/auth.js';
import { hasPermission } from '../lib/permissions.js';
import { api } from '../lib/api.js';

// ---------- Helpers ----------
function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return iso; }
}
function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('vi-VN', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}
function fmtVnd(v) {
  if (v === null || v === undefined) return '—';
  return Number(v).toLocaleString('vi-VN') + 'đ';
}
function daysBetween(iso, now = new Date()) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return Math.floor((now.getTime() - d.getTime()) / 86400_000);
  } catch { return null; }
}

const ALERT_LABEL = {
  inactive_3d:   'Không học ≥3 ngày',
  low_accuracy:  'Tỉ lệ đúng <50%',
  high_cost:     'Chi phí AI cao',
};

// ============================================================
// Tab 1: Overview
// ============================================================
function OverviewTab({ onClickEmployee }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('best_score_month');
  const [sortDir, setSortDir] = useState('desc');

  async function load() {
    setLoading(true);
    try {
      const r = await api.progressOverview();
      setData(r);
    } catch (e) {
      toast.error('Lỗi tải tổng quan: ' + (e?.message || e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  const sortedRows = useMemo(() => {
    if (!data?.rows) return [];
    const rows = [...data.rows];
    rows.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === 'string') {
        return sortDir === 'asc' ? av.localeCompare(bv, 'vi') : bv.localeCompare(av, 'vi');
      }
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return rows;
  }, [data, sortKey, sortDir]);

  function clickSort(key) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  }

  function SortHeader({ col, label }) {
    const active = sortKey === col;
    return (
      <th
        onClick={() => clickSort(col)}
        className="px-2 py-2 text-left text-xs font-semibold cursor-pointer hover:bg-slate-100 select-none"
      >
        {label}
        {active ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
      </th>
    );
  }

  if (loading) return <div className="text-slate-500 p-4">Đang tải tổng quan…</div>;
  if (!data) return <div className="text-red-600 p-4">Không tải được dữ liệu.</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-slate-500">
          {sortedRows.length} nhân viên · Chốt lúc {fmtDateTime(data.generated_at)}
        </div>
        <div className="flex items-center gap-3">
          {/* L6 (2026-04-24): Export CSV — fetch với JWT rồi download blob */}
          <button
            onClick={async () => {
              try {
                const token = useAuthStore.getState().accessToken;
                const res = await fetch('/api/admin/progress/overview.csv', {
                  headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `tien-trinh-nv-${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(link);
                link.click();
                link.remove();
                URL.revokeObjectURL(url);
                toast.success('Đã tải CSV');
              } catch (err) {
                toast.error('Export thất bại: ' + (err.message || err));
              }
            }}
            className="text-sm text-emerald-600 hover:underline"
            title="Tải bảng tổng quan về Excel/CSV"
          >📥 Export CSV</button>
          <button onClick={load} className="text-sm text-brand-600 hover:underline">⟳ Tải lại</button>
        </div>
      </div>
      <div className="overflow-x-auto border rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <SortHeader col="full_name" label="Nhân viên" />
              <SortHeader col="shop_name" label="Shop" />
              <SortHeader col="best_score_month" label="Best (tháng)" />
              <SortHeader col="last_learn_at" label="Ngày học cuối" />
              <SortHeader col="cards_mastered" label="Card thuộc" />
              <SortHeader col="accuracy_pct" label="Tỉ lệ đúng" />
              <th className="px-2 py-2 text-left text-xs font-semibold">Bộ đã đậu</th>
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((r) => {
              const dSince = daysBetween(r.last_learn_at);
              const stale = dSince !== null && dSince >= 3;
              const low = r.accuracy_pct !== null && r.accuracy_pct < 50;
              return (
                <tr key={r.employee_id} className="border-b hover:bg-slate-50">
                  <td className="px-2 py-2 font-medium">{r.full_name}</td>
                  <td className="px-2 py-2 text-slate-500">{r.shop_name || '—'}</td>
                  <td className="px-2 py-2">
                    {r.best_score_month !== null
                      ? <span className="font-mono">{r.best_score_month}</span>
                      : <span className="text-slate-400">—</span>}
                  </td>
                  <td className={`px-2 py-2 ${stale ? 'text-red-600 font-semibold' : ''}`}>
                    {fmtDate(r.last_learn_at)}
                    {dSince !== null && <span className="text-xs text-slate-400 ml-1">({dSince}d)</span>}
                  </td>
                  <td className="px-2 py-2 font-mono">{r.cards_mastered}</td>
                  <td className={`px-2 py-2 ${low ? 'text-red-600 font-semibold' : ''}`}>
                    {r.accuracy_pct !== null ? `${r.accuracy_pct}%` : '—'}
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex gap-1 text-xs">
                      {r.reward_status?.bo_1000k && <span title="bo_1000k" className="inline-block px-1.5 py-0.5 bg-emerald-100 text-emerald-800 rounded">500K</span>}
                      {r.reward_status?.bo_1250k && <span title="bo_1250k" className="inline-block px-1.5 py-0.5 bg-emerald-100 text-emerald-800 rounded">1M</span>}
                      {r.reward_status?.bo_nang_cao && <span title="bo_nang_cao" className="inline-block px-1.5 py-0.5 bg-purple-100 text-purple-800 rounded">1.25M</span>}
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    <button
                      onClick={() => onClickEmployee(r.employee_id)}
                      className="text-brand-600 hover:underline text-xs"
                    >
                      Chi tiết →
                    </button>
                  </td>
                </tr>
              );
            })}
            {sortedRows.length === 0 && (
              <tr><td colSpan={8} className="px-2 py-8 text-center text-slate-400">Không có NV nào.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================
// Tab 2: Detail (có modal reset topic — AC-FC-6)
// ============================================================
function DetailTab({ employeeId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetModal, setResetModal] = useState(null); // { setName, topic }
  const [remindModal, setRemindModal] = useState(null); // alert-like object, type=null → admin chọn

  async function load() {
    setLoading(true);
    try {
      const r = await api.progressEmployeeDetail(employeeId);
      setData(r);
    } catch (e) {
      toast.error('Lỗi tải chi tiết: ' + (e?.message || e));
    } finally { setLoading(false); }
  }
  useEffect(() => { if (employeeId) load(); }, [employeeId]);

  if (!employeeId) {
    return (
      <div className="text-slate-500 p-4 border rounded bg-slate-50 text-sm">
        Vào tab <b>Tổng quan</b> và bấm <b>Chi tiết →</b> trên một NV để xem lịch sử.
      </div>
    );
  }
  if (loading) return <div className="text-slate-500 p-4">Đang tải chi tiết…</div>;
  if (!data) return <div className="text-red-600 p-4">Không tải được dữ liệu.</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold">{data.employee.full_name}</h3>
          <div className="text-sm text-slate-500">
            Shop: {data.employee.shop_name || '—'} · Trạng thái: {data.employee.status}
            · Bộ đã đậu: {data.passed_bos.length > 0 ? data.passed_bos.join(', ') : 'Chưa có'}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => api.progressEmployeeExamHistoryPdf(employeeId)}
            className="px-3 py-1 text-sm bg-slate-600 text-white rounded hover:bg-slate-700"
            title="Tải file PDF lịch sử kiểm tra của NV"
          >
            📄 Xuất PDF
          </button>
          <button
            onClick={() => setRemindModal({
              employee_id: employeeId,
              full_name: data.employee.full_name,
              type: null,
              suggested_message: '',
            })}
            className="px-3 py-1 text-sm bg-brand-600 text-white rounded hover:bg-brand-700"
            title="Gửi nhắc nhở tới NV qua Telegram"
          >
            📨 Gửi nhắc nhở Telegram
          </button>
          <button onClick={onClose} className="text-sm text-slate-500 hover:text-slate-800">← Quay lại</button>
        </div>
      </div>

      {/* Lịch sử thi bộ */}
      <section className="border rounded p-3">
        <h4 className="font-semibold mb-2">Lịch sử thi bộ (20 gần nhất)</h4>
        {data.exam_history.length === 0 ? (
          <div className="text-slate-400 text-sm">Chưa có lần thi nào.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-2 py-1 text-left">Bộ</th>
                  <th className="px-2 py-1 text-left">Điểm</th>
                  <th className="px-2 py-1 text-left">Đậu?</th>
                  <th className="px-2 py-1 text-left">Bắt đầu</th>
                  <th className="px-2 py-1 text-left">Nộp lúc</th>
                  <th className="px-2 py-1 text-left">Grader</th>
                </tr>
              </thead>
              <tbody>
                {data.exam_history.map(e => (
                  <tr key={e.id} className="border-b">
                    <td className="px-2 py-1 font-mono">{e.bo}</td>
                    <td className="px-2 py-1">{e.score !== null ? e.score : '—'}</td>
                    <td className="px-2 py-1">{e.passed ? '✅' : (e.status === 'submitted' ? '❌' : '⏸')}</td>
                    <td className="px-2 py-1">{fmtDateTime(e.started_at)}</td>
                    <td className="px-2 py-1">{fmtDateTime(e.submitted_at)}</td>
                    <td className="px-2 py-1 text-slate-500">{e.grader_model || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Rules exam */}
      <section className="border rounded p-3">
        <h4 className="font-semibold mb-2">Thi nội quy (10 gần nhất)</h4>
        {data.rules_exam_history.length === 0 ? (
          <div className="text-slate-400 text-sm">Chưa thi nội quy.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-2 py-1 text-left">Version</th>
                  <th className="px-2 py-1 text-left">Điểm</th>
                  <th className="px-2 py-1 text-left">Đậu?</th>
                  <th className="px-2 py-1 text-left">Bắt đầu</th>
                </tr>
              </thead>
              <tbody>
                {data.rules_exam_history.map(r => (
                  <tr key={r.id} className="border-b">
                    <td className="px-2 py-1 font-mono">v{r.rules_version_id}</td>
                    <td className="px-2 py-1">{r.score_pct !== null ? `${r.score_pct}%` : '—'}</td>
                    <td className="px-2 py-1">{r.passed ? '✅' : '❌'}</td>
                    <td className="px-2 py-1">{fmtDateTime(r.started_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Ask AI */}
      <section className="border rounded p-3">
        <h4 className="font-semibold mb-2">Hỏi đáp (30 gần nhất)</h4>
        {data.ask_history.length === 0 ? (
          <div className="text-slate-400 text-sm">Chưa hỏi AI lần nào.</div>
        ) : (
          <ul className="space-y-1 text-xs max-h-72 overflow-y-auto">
            {data.ask_history.map(a => (
              <li key={a.id} className="border-b pb-1">
                <div className="flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                    a.case_type === 'auto_answer' ? 'bg-emerald-100 text-emerald-800' :
                    a.case_type === 'candidate_list' ? 'bg-sky-100 text-sky-800' :
                    'bg-amber-100 text-amber-800'}`}>
                    {a.case_type || '—'}
                  </span>
                  <span className="text-slate-400">{fmtDateTime(a.created_at)}</span>
                  <span className="text-slate-500">· step {a.step}</span>
                  {a.cost_vnd > 0 && <span className="text-slate-500">· {fmtVnd(a.cost_vnd)}</span>}
                  {/* 2026-04-24: BE lưu 'up'/'down' (text, migration 007), không phải 1/-1 */}
                  {a.feedback === 'up' && <span title="NV flag Đúng">👍</span>}
                  {a.feedback === 'down' && <span title="NV flag Sai">👎</span>}
                </div>
                <div className="text-slate-700 truncate">{a.question_text}</div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Flashcard timeline */}
      <section className="border rounded p-3">
        <h4 className="font-semibold mb-2">Flashcard timeline (14 ngày)</h4>
        {data.flashcard_timeline.length === 0 ? (
          <div className="text-slate-400 text-sm">Không có review nào trong 14 ngày.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-2 py-1 text-left">Ngày</th>
                  <th className="px-2 py-1 text-left">Tổng review</th>
                  <th className="px-2 py-1 text-left">Đúng (q≥3)</th>
                  <th className="px-2 py-1 text-left">Tỉ lệ</th>
                </tr>
              </thead>
              <tbody>
                {data.flashcard_timeline.map(t => (
                  <tr key={t.date} className="border-b">
                    <td className="px-2 py-1 font-mono">{t.date}</td>
                    <td className="px-2 py-1">{t.reviews}</td>
                    <td className="px-2 py-1">{t.correct}</td>
                    <td className="px-2 py-1">
                      {t.reviews > 0 ? `${Math.round((t.correct / t.reviews) * 100)}%` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Weak topics + reset button */}
      <section className="border rounded p-3">
        <h4 className="font-semibold mb-2">Topic yếu (&lt;70% đúng, ≥3 card)</h4>
        {data.weak_topics.length === 0 ? (
          <div className="text-slate-400 text-sm">Không có topic yếu.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-2 py-1 text-left">Bộ</th>
                  <th className="px-2 py-1 text-left">Topic</th>
                  <th className="px-2 py-1 text-left">Card</th>
                  <th className="px-2 py-1 text-left">Đúng</th>
                  <th className="px-2 py-1 text-left">Tỉ lệ</th>
                  <th className="px-2 py-1"></th>
                </tr>
              </thead>
              <tbody>
                {data.weak_topics.map((t, idx) => (
                  <tr key={`${t.set_name}-${t.topic}-${idx}`} className="border-b">
                    <td className="px-2 py-1 font-mono">{t.set_name}</td>
                    <td className="px-2 py-1">{t.topic}</td>
                    <td className="px-2 py-1">{t.total}</td>
                    <td className="px-2 py-1">{t.correct}</td>
                    <td className="px-2 py-1 text-red-600 font-semibold">{t.accuracy_pct}%</td>
                    <td className="px-2 py-1">
                      <button
                        onClick={() => setResetModal({ setName: t.set_name, topic: t.topic === '—' ? null : t.topic })}
                        className="text-xs text-brand-600 hover:underline"
                      >
                        Reset topic
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="text-xs text-slate-400 mt-2">
          Reset topic (AC-FC-6): xoá lịch sử học của topic → NV phải học lại từ đầu. Yêu cầu TOTP admin.
        </div>
      </section>

      {resetModal && (
        <ResetTopicModal
          employeeId={employeeId}
          employeeName={data.employee.full_name}
          setName={resetModal.setName}
          topic={resetModal.topic}
          onClose={() => setResetModal(null)}
          onDone={() => { setResetModal(null); load(); toast.success('Đã reset topic'); }}
        />
      )}
      {remindModal && (
        <RemindModal
          alert={remindModal}
          onClose={() => setRemindModal(null)}
          onDone={() => { setRemindModal(null); toast.success('Đã gửi nhắc nhở'); }}
        />
      )}
    </div>
  );
}

// Modal reset topic (AC-FC-6) — dùng X-TOTP-Code header
function ResetTopicModal({ employeeId, employeeName, setName, topic, onClose, onDone }) {
  const [totp, setTotp] = useState('');
  const [sending, setSending] = useState(false);

  async function submit() {
    const code = totp.trim();
    if (!/^\d{6}$/.test(code)) {
      toast.error('TOTP phải 6 số');
      return;
    }
    setSending(true);
    try {
      await api.adminFlashcardResetTopic({ employeeId, setName, topic, totpCode: code });
      onDone();
    } catch (e) {
      toast.error('Reset thất bại: ' + (e?.message || e));
    } finally { setSending(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5">
        <h4 className="text-lg font-semibold mb-2">Reset topic?</h4>
        <div className="text-sm text-slate-700 space-y-1 mb-4">
          <div>NV: <b>{employeeName}</b></div>
          <div>Bộ: <span className="font-mono">{setName}</span></div>
          <div>Topic: <span className="font-mono">{topic || '(tất cả)'}</span></div>
          <div className="text-red-600 mt-2">
            ⚠️ Thao tác này sẽ xoá toàn bộ lịch sử học của topic. NV sẽ phải học lại từ đầu.
          </div>
        </div>
        <div className="mb-3">
          <label className="block text-xs text-slate-600 mb-1">Nhập TOTP admin (6 số)</label>
          <input
            type="text" inputMode="numeric" maxLength={6} autoFocus
            value={totp}
            onChange={(e) => setTotp(e.target.value.replace(/\D/g, ''))}
            className="w-full border rounded px-2 py-1 font-mono text-lg"
            placeholder="000000"
          />
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} disabled={sending} className="px-3 py-1 text-sm border rounded hover:bg-slate-50">
            Huỷ
          </button>
          <button
            onClick={submit}
            disabled={sending || !/^\d{6}$/.test(totp.trim())}
            className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
          >
            {sending ? 'Đang reset…' : 'Xác nhận reset'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Tab 3: Leaderboard
// ============================================================
function LeaderboardTab() {
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load(p = period) {
    setLoading(true);
    try {
      const r = await api.progressLeaderboard(p);
      setData(r);
    } catch (e) {
      toast.error('Lỗi tải ranking: ' + (e?.message || e));
    } finally { setLoading(false); }
  }
  useEffect(() => { load(period); }, [period]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        {['week', 'month', 'quarter'].map(p => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            className={`px-3 py-1 text-sm rounded border ${
              period === p
                ? 'bg-brand-600 text-white border-brand-600'
                : 'bg-white text-slate-700 hover:bg-slate-50'}`}
          >
            {p === 'week' ? 'Tuần' : p === 'month' ? 'Tháng' : 'Quý'}
          </button>
        ))}
        {data && (
          <span className="text-xs text-slate-500 ml-2">
            Từ {fmtDate(data.period_start)} · Chốt {fmtDateTime(data.generated_at)}
          </span>
        )}
      </div>
      {loading ? (
        <div className="text-slate-500 p-4">Đang tải…</div>
      ) : !data ? (
        <div className="text-red-600 p-4">Không tải được dữ liệu.</div>
      ) : (
        <div className="overflow-x-auto border rounded">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-2 py-2 text-left text-xs font-semibold">#</th>
                <th className="px-2 py-2 text-left text-xs font-semibold">Nhân viên</th>
                <th className="px-2 py-2 text-left text-xs font-semibold">Shop</th>
                <th className="px-2 py-2 text-left text-xs font-semibold">Tổng điểm</th>
                <th className="px-2 py-2 text-left text-xs font-semibold">Exam</th>
                <th className="px-2 py-2 text-left text-xs font-semibold">Đậu bộ</th>
                <th className="px-2 py-2 text-left text-xs font-semibold">Ack nội quy</th>
                <th className="px-2 py-2 text-left text-xs font-semibold">Đậu thi nội quy</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map(r => (
                <tr key={r.employee_id} className="border-b hover:bg-slate-50">
                  <td className="px-2 py-2 font-semibold">
                    {r.badge ? <span className="text-xl">{r.badge}</span> : r.rank}
                  </td>
                  <td className="px-2 py-2 font-medium">{r.full_name}</td>
                  <td className="px-2 py-2 text-slate-500">{r.shop_name || '—'}</td>
                  <td className="px-2 py-2 font-mono font-bold text-brand-700">{r.total_points}</td>
                  <td className="px-2 py-2 text-xs text-slate-500">
                    {r.exam_score_sum} ({r.exam_count}×)
                  </td>
                  <td className="px-2 py-2">{r.exam_passes}</td>
                  <td className="px-2 py-2">{r.rules_ack_count}</td>
                  <td className="px-2 py-2">{r.rules_exam_passes}</td>
                </tr>
              ))}
              {data.rows.length === 0 && (
                <tr><td colSpan={8} className="px-2 py-8 text-center text-slate-400">Không có NV nào.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <div className="text-xs text-slate-400 mt-2">
        Công thức điểm: SUM(exam score) + 10 × (ack nội quy) + 5 × (đậu thi nội quy).
      </div>
    </div>
  );
}

// ============================================================
// Tab 4: Alerts + modal remind
// ============================================================
function AlertsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [remindModal, setRemindModal] = useState(null); // alert object

  async function load() {
    setLoading(true);
    try {
      const r = await api.progressAlerts();
      setData(r);
    } catch (e) {
      toast.error('Lỗi tải cảnh báo: ' + (e?.message || e));
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-slate-500 p-4">Đang tải cảnh báo…</div>;
  if (!data) return <div className="text-red-600 p-4">Không tải được dữ liệu.</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-slate-500">
          {data.alerts.length} cảnh báo · Trung bình cost team: {fmtVnd(data.team_avg_cost_vnd)}
          · Chốt {fmtDateTime(data.generated_at)}
        </div>
        <button onClick={load} className="text-sm text-brand-600 hover:underline">⟳ Tải lại</button>
      </div>
      {data.alerts.length === 0 ? (
        <div className="p-6 text-center text-slate-500 border rounded bg-emerald-50">
          ✅ Không có cảnh báo nào — team đang ổn.
        </div>
      ) : (
        <ul className="space-y-2">
          {data.alerts.map((a, idx) => (
            <li key={`${a.employee_id}-${a.type}-${idx}`} className="border rounded p-3 flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                    a.type === 'inactive_3d' ? 'bg-amber-100 text-amber-800' :
                    a.type === 'low_accuracy' ? 'bg-red-100 text-red-800' :
                    'bg-purple-100 text-purple-800'}`}>
                    {ALERT_LABEL[a.type] || a.type}
                  </span>
                  <span className="font-semibold">{a.full_name}</span>
                </div>
                <div className="text-xs text-slate-500 mb-1">
                  {a.type === 'inactive_3d' && `Không hoạt động: ${a.details.days}d${a.details.last_activity ? ` (cuối: ${fmtDateTime(a.details.last_activity)})` : ''}`}
                  {a.type === 'low_accuracy' && `Avg 3 exam: ${a.details.accuracy_pct}% · điểm: ${(a.details.last_3_scores || []).join(', ')}`}
                  {a.type === 'high_cost' && `Cost: ${fmtVnd(a.details.cost_vnd)} · gấp ${a.details.ratio}× avg (${fmtVnd(a.details.team_avg_vnd)})`}
                </div>
                <div className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 p-2 rounded border">
                  {a.suggested_message}
                </div>
              </div>
              <div className="shrink-0">
                <button
                  onClick={() => setRemindModal(a)}
                  className="px-3 py-1 text-sm bg-brand-600 text-white rounded hover:bg-brand-700"
                >
                  Gửi Telegram
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {remindModal && (
        <RemindModal
          alert={remindModal}
          onClose={() => setRemindModal(null)}
          onDone={() => { setRemindModal(null); toast.success('Đã gửi nhắc nhở'); }}
        />
      )}
    </div>
  );
}

// Modal confirm gửi Telegram
// Nếu alert.type === null → admin được chọn loại (gọi từ DetailTab).
// Nếu alert.type đã có → loại cố định + suggested_message prefill (gọi từ AlertsTab).
function RemindModal({ alert, onClose, onDone }) {
  const pickableType = alert.type === null || alert.type === undefined;
  const [type, setType] = useState(alert.type || 'inactive_3d');
  const [msg, setMsg] = useState(alert.suggested_message || '');
  const [sending, setSending] = useState(false);

  // Template gợi ý khi admin đổi loại (chỉ trong chế độ picker)
  function typeTemplate(t) {
    const name = alert.full_name;
    switch (t) {
      case 'inactive_3d':
        return `Xin chào ${name},\nAnh/chị đã không học trong 3 ngày gần đây. Vui lòng dành 15-20 phút ôn tập hôm nay nhé. Cảm ơn!`;
      case 'low_accuracy':
        return `Xin chào ${name},\nTỉ lệ đúng các bài thi gần nhất hơi thấp. Hãy xem lại các topic yếu trước khi thi tiếp nhé.`;
      case 'high_cost':
        return `Xin chào ${name},\nChi phí hỏi AI tháng này hơi cao. Gợi ý: tìm trong bộ câu hỏi có sẵn trước khi hỏi AI.`;
      default: return '';
    }
  }

  function changeType(t) {
    setType(t);
    // Chỉ auto-fill khi textarea trống hoặc đang là template của loại cũ
    if (pickableType && (!msg.trim() || Object.values(ALERT_LABEL).some(() => false))) {
      setMsg(typeTemplate(t));
    }
  }

  async function submit() {
    const m = msg.trim();
    if (m.length < 10 || m.length > 2000) {
      toast.error('Message phải 10-2000 ký tự');
      return;
    }
    setSending(true);
    try {
      const r = await api.progressRemind({
        employee_id: alert.employee_id,
        alert_type: type,
        message: m,
      });
      if (r?.recipients === 0) {
        toast((t) => (
          <span>
            Đã audit ✅<br/>
            Nhưng chưa có recipient Telegram.<br/>
            <Link to="/admin/telegram" className="text-brand-600 underline" onClick={() => toast.dismiss(t.id)}>
              Cấu hình Telegram →
            </Link>
          </span>
        ), { duration: 6000 });
      }
      onDone();
    } catch (e) {
      toast.error('Gửi thất bại: ' + (e?.message || e));
    } finally { setSending(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-5">
        <h4 className="text-lg font-semibold mb-2">Gửi nhắc nhở Telegram</h4>
        <div className="text-sm text-slate-700 mb-3">
          NV: <b>{alert.full_name}</b>
          {!pickableType && (
            <> · Loại: <b>{ALERT_LABEL[type] || type}</b></>
          )}
        </div>

        {pickableType && (
          <div className="mb-3">
            <label className="block text-xs text-slate-600 mb-1">Loại nhắc nhở</label>
            <select
              value={type}
              onChange={(e) => changeType(e.target.value)}
              className="w-full border rounded px-2 py-1 text-sm"
            >
              <option value="inactive_3d">Không học ≥3 ngày</option>
              <option value="low_accuracy">Tỉ lệ đúng thấp</option>
              <option value="high_cost">Chi phí AI cao</option>
            </select>
            <div className="text-xs text-slate-400 mt-1">
              Chọn loại gần nhất với lý do nhắc — BE yêu cầu 1 trong 3 enum (audit + routing Telegram).
            </div>
          </div>
        )}

        <label className="block text-xs text-slate-600 mb-1">Nội dung tin nhắn (10-2000 ký tự)</label>
        <textarea
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          rows={7}
          className="w-full border rounded px-2 py-1 text-sm font-mono"
          placeholder={pickableType ? 'Nhập nội dung hoặc chọn loại để lấy template gợi ý…' : ''}
        />
        <div className="text-xs text-slate-400 mt-1">{msg.length} ký tự</div>
        <div className="flex justify-end gap-2 mt-3">
          <button onClick={onClose} disabled={sending} className="px-3 py-1 text-sm border rounded hover:bg-slate-50">
            Huỷ
          </button>
          <button
            onClick={submit}
            disabled={sending || msg.trim().length < 10}
            className="px-3 py-1 text-sm bg-brand-600 text-white rounded hover:bg-brand-700 disabled:opacity-50"
          >
            {sending ? 'Đang gửi…' : 'Gửi ngay'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Tab 5: Feedback AI — 2026-04-24
// Tổng hợp Đúng/Sai toàn shop trong N ngày, list câu bị "Sai" để admin sửa.
// ============================================================
function AiFeedbackTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  async function load(d = days) {
    setLoading(true);
    try {
      const r = await api.progressAiFeedback(d);
      setData(r);
    } catch (e) {
      toast.error('Lỗi tải feedback AI: ' + (e?.message || e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(days); }, [days]);

  if (loading) return <div className="text-sm text-slate-500">Đang tải…</div>;
  if (!data) return <div className="text-sm text-slate-400">Không có dữ liệu.</div>;

  const s = data.summary;
  const pctBar = (n, total) => total > 0 ? `${Math.round((n/total)*100)}%` : '0%';

  return (
    <div className="space-y-4">
      {/* Top control */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-base font-semibold">📊 Feedback AI từ nhân viên</h3>
          <p className="text-xs text-slate-500">
            Tổng hợp các câu AI trả lời bị NV bấm Sai — admin xem để sửa KB / prompts / câu hỏi mẫu.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-600">Thời gian:</label>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="border rounded px-2 py-1 text-sm"
          >
            <option value={7}>7 ngày</option>
            <option value={30}>30 ngày</option>
            <option value={90}>90 ngày</option>
          </select>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-slate-50 border rounded p-3">
          <div className="text-xs text-slate-500">Tổng câu hỏi</div>
          <div className="text-2xl font-bold text-slate-800">{s.total}</div>
          <div className="text-xs text-slate-400 mt-0.5">{data.period_days} ngày</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
          <div className="text-xs text-emerald-600">👍 Đúng</div>
          <div className="text-2xl font-bold text-emerald-700">{s.up}</div>
          <div className="text-xs text-emerald-500 mt-0.5">{s.up_rate_pct}% (trong số có feedback)</div>
        </div>
        <div className="bg-rose-50 border border-rose-200 rounded p-3">
          <div className="text-xs text-rose-600">👎 Sai</div>
          <div className="text-2xl font-bold text-rose-700">{s.down}</div>
          <div className="text-xs text-rose-500 mt-0.5">{s.down_rate_pct}% (trong số có feedback)</div>
        </div>
        <div className="bg-slate-50 border rounded p-3">
          <div className="text-xs text-slate-500">Chưa feedback</div>
          <div className="text-2xl font-bold text-slate-600">{s.no_feedback}</div>
          <div className="text-xs text-slate-400 mt-0.5">{pctBar(s.no_feedback, s.total)}</div>
        </div>
      </div>

      {/* Trend 7 ngày — bar chart đơn giản inline */}
      {data.trend7d.length > 0 && (
        <div className="border rounded p-3">
          <h4 className="text-sm font-semibold mb-2">📈 Xu hướng 7 ngày gần nhất</h4>
          <div className="space-y-1">
            {data.trend7d.map((t) => {
              const total = t.up + t.down;
              const upPct = total > 0 ? (t.up / total) * 100 : 0;
              return (
                <div key={t.date} className="flex items-center gap-2 text-xs">
                  <span className="w-20 text-slate-500">{t.date.slice(5)}</span>
                  <div className="flex-1 flex h-4 rounded overflow-hidden bg-slate-100">
                    <div className="bg-emerald-400" style={{ width: `${upPct}%` }} title={`👍 ${t.up}`} />
                    <div className="bg-rose-400" style={{ width: `${100 - upPct}%` }} title={`👎 ${t.down}`} />
                  </div>
                  <span className="w-24 text-right text-slate-500">
                    👍 {t.up} · 👎 {t.down}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* List câu bị Sai */}
      <div className="border rounded">
        <div className="px-3 py-2 border-b bg-slate-50 text-sm font-semibold">
          👎 Các câu AI bị flag SAI ({data.bad_items.length} câu gần nhất)
        </div>
        {data.bad_items.length === 0 ? (
          <div className="p-4 text-sm text-slate-400 text-center">
            Tuyệt vời! Không có câu nào bị flag Sai trong {data.period_days} ngày qua. 🎉
          </div>
        ) : (
          <div className="divide-y max-h-[600px] overflow-y-auto">
            {data.bad_items.map((it) => (
              <div key={it.id} className="p-3 hover:bg-slate-50">
                <div className="flex items-center gap-2 text-xs mb-1 flex-wrap">
                  <span className={`px-1.5 py-0.5 rounded font-mono ${
                    it.case_type === 'auto_answer' ? 'bg-emerald-100 text-emerald-800' :
                    it.case_type === 'candidate_list' ? 'bg-sky-100 text-sky-800' :
                    'bg-amber-100 text-amber-800'}`}>
                    {it.case_type || '—'}
                  </span>
                  <span className="text-slate-500">step {it.step}</span>
                  <span className="text-slate-400">·</span>
                  <span className="text-slate-600 font-medium">{it.employee_name || '—'}</span>
                  {it.shop_code && <span className="text-slate-400 uppercase">({it.shop_code})</span>}
                  <span className="text-slate-400">·</span>
                  <span className="text-slate-400">{fmtDateTime(it.created_at)}</span>
                  {it.cost_vnd > 0 && (
                    <>
                      <span className="text-slate-400">·</span>
                      <span className="text-slate-500">{fmtVnd(it.cost_vnd)}</span>
                    </>
                  )}
                  {it.matched_question_id && (
                    <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px]">
                      🎯 KB id={it.matched_question_id}
                    </span>
                  )}
                  {it.saved_to_cho_duyet === 1 && (
                    <span className="px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 text-[10px]">
                      📝 đã sinh vào cho_duyet
                    </span>
                  )}
                </div>
                <div className="text-sm text-slate-800 font-medium">❓ {it.question_text}</div>
                {it.answer_text && (
                  <div className="text-xs text-slate-600 mt-1 italic line-clamp-3">
                    💬 {it.answer_text}
                  </div>
                )}
                {/* Hint hành động cho admin */}
                <div className="text-xs text-slate-500 mt-1">
                  {it.case_type === 'auto_answer' && it.matched_question_id ? (
                    <>👉 Là câu từ KB — vào <Link to="/admin/questions" className="text-brand-600 hover:underline">Câu hỏi (6 bộ)</Link> tìm id={it.matched_question_id} để sửa đáp án.</>
                  ) : it.saved_to_cho_duyet === 1 ? (
                    <>👉 AI sinh câu vào cho_duyet — vào <Link to="/admin/questions" className="text-brand-600 hover:underline">Câu hỏi (6 bộ)</Link> bộ Chờ duyệt để review trước khi duyệt.</>
                  ) : (
                    <>👉 Cân nhắc bổ sung KB / điều chỉnh prompts để AI trả lời tốt hơn.</>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Main page
// ============================================================
export default function AdminProgressPage() {
  const user = useAuthStore(s => s.user);
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview'); // overview | detail | leaderboard | alerts | ai-feedback
  const [detailId, setDetailId] = useState(null);

  if (!hasPermission(user, 'progress')) {
    return <div className="p-6 text-red-600">403 — bạn không có quyền xem trang này.</div>;
  }

  const TABS = [
    { k: 'overview',    label: 'Tổng quan' },
    { k: 'detail',      label: 'Chi tiết NV' },
    { k: 'leaderboard', label: 'Xếp hạng' },
    { k: 'alerts',      label: 'Cảnh báo' },
    { k: 'ai-feedback', label: '👎 Feedback AI' },  // 2026-04-24
  ];

  function openDetail(empId) {
    setDetailId(empId);
    setTab('detail');
  }
  function closeDetail() {
    setDetailId(null);
    setTab('overview');
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/admin" className="text-sm text-slate-500 hover:text-slate-700 shrink-0">← Trang admin</Link>
          <h1 className="text-base md:text-lg font-semibold truncate">Tiến trình nhân viên</h1>
          <span className="text-xs text-slate-400 hidden md:inline">M12.A · PRD §7.6</span>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-3 md:p-4">
        {/* M148c (2026-05-12): tabs horizontal scroll trên mobile (4-5 tabs không fit width) */}
        <div className="flex gap-1 mb-4 border-b overflow-x-auto scrollbar-hide -mx-3 px-3 md:mx-0 md:px-0">
          {TABS.map(t => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`tap-feedback shrink-0 px-3 md:px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                tab === t.k
                  ? 'border-brand-600 text-brand-700'
                  : 'border-transparent text-slate-600 hover:text-slate-800'}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-3 md:p-4">
          {tab === 'overview'    && <OverviewTab onClickEmployee={openDetail} />}
          {tab === 'detail'      && <DetailTab employeeId={detailId} onClose={closeDetail} />}
          {tab === 'leaderboard' && <LeaderboardTab />}
          {tab === 'alerts'      && <AlertsTab />}
          {tab === 'ai-feedback' && <AiFeedbackTab />}
        </div>
      </main>
    </div>
  );
}
